import { isDefined } from "@clipboard-health/util-ts";

import { clearSessionStorage } from "../../../utils/clearSessionStorage";
import { APP_V3_APP_EVENTS, logEvent } from "../../lib/analytics";
import { clearRedirectInProgress } from "./redirectInProgress";

export const SESSION_HYDRATION_RECOVERY_TIMEOUT_MS = 30_000;

const SESSION_HYDRATION_RECOVERY_RELOAD_KEY = "cognito_session_hydration_recovery_reload";

type SessionHydrationSource = "login" | "magic-link" | "startup-retry";

interface RunSessionHydrationWithReloadRecoveryArguments<T> {
  enabled: boolean;
  hydrate: () => Promise<T>;
  source: SessionHydrationSource;
}

export async function runSessionHydrationWithReloadRecovery<T>({
  enabled,
  hydrate,
  source,
}: RunSessionHydrationWithReloadRecoveryArguments<T>): Promise<T | undefined> {
  const hydrationPromise = hydrate();
  if (!enabled) {
    return await hydrationPromise;
  }

  let timeoutId: ReturnType<typeof globalThis.setTimeout> | undefined;
  const recoveryPromise = new Promise<undefined>((resolve) => {
    timeoutId = globalThis.setTimeout(() => {
      const hasAlreadyReloaded = hasSessionHydrationRecoveryReloaded();
      logEvent(APP_V3_APP_EVENTS.SESSION_HYDRATION_RECOVERY, {
        action: hasAlreadyReloaded ? "reset-session-and-reload" : "reload",
        source,
        trigger: "timeout",
      });

      if (hasAlreadyReloaded) {
        clearSessionAfterFailedHydrationRecovery();
      } else {
        sessionStorage.setItem(SESSION_HYDRATION_RECOVERY_RELOAD_KEY, "1");
      }

      // Resolve first so callers stop their post-hydration work even in
      // environments where a reload is intercepted or unavailable.
      resolve(undefined);
      globalThis.location.reload();
    }, SESSION_HYDRATION_RECOVERY_TIMEOUT_MS);
  });

  try {
    return await Promise.race([hydrationPromise, recoveryPromise]);
  } finally {
    if (isDefined(timeoutId)) {
      globalThis.clearTimeout(timeoutId);
    }
  }
}

export function hasSessionHydrationRecoveryReloaded(): boolean {
  return sessionStorage.getItem(SESSION_HYDRATION_RECOVERY_RELOAD_KEY) === "1";
}

export function completeSessionHydrationReloadRecovery(): void {
  sessionStorage.removeItem(SESSION_HYDRATION_RECOVERY_RELOAD_KEY);
  clearRedirectInProgress();
}

export function resetSessionAfterFailedHydrationRecovery(): void {
  logEvent(APP_V3_APP_EVENTS.SESSION_HYDRATION_RECOVERY, {
    action: "reset-session-and-reload",
    source: "startup-retry",
    trigger: "retry-not-hydrated",
  });
  clearSessionAfterFailedHydrationRecovery();
  globalThis.location.reload();
}

function clearSessionAfterFailedHydrationRecovery(): void {
  clearSessionStorage();
  completeSessionHydrationReloadRecovery();
}
